import {
  BUDDY_HALF_WIDTH,
  BUDDY_HEIGHT,
  type BuddyEffect,
  type BuddyPose,
  drawBulb,
  drawQuestionMark,
  drawSleepZs,
  drawSparkle,
  easeBack,
  easeInOutCubic,
  hop,
  raiseArm,
  segment,
  topOf,
} from './buddyEngine';

const H = BUDDY_HEIGHT;
const H2 = H / 2;

type Behaviour = (A: BuddyPose, I: BuddyPose, t: number, fx: BuddyEffect[]) => void;

const idle: Behaviour = (A, I, t) => {
  const breath = Math.sin(t * 2) * 0.012;
  A.sy = 1 + breath;
  I.sy = 1 - breath;
  const open = t % 3.4 > 3.25 ? 0 : 1;
  A.open = open;
  I.open = open;
  A.look = [Math.sin(t * 0.6), Math.cos(t * 0.45) * 0.4];
  I.look = [Math.sin(t * 0.6 + 1), Math.cos(t * 0.5) * 0.4];
};

const listen: Behaviour = (A, I, t) => {
  A.look = [1, -0.7];
  I.look = [1, -0.7];
  A.mouth = 'o';
  I.mouth = 'o';
  raiseArm(I, 1, [I.cx + 6, -H - 2 + Math.sin(t * 6) * 0.6]);
};

const greet: Behaviour = (A, I, t) => {
  const wave = Math.sin(t * 9);
  raiseArm(A, 1, [A.cx + 9 + wave * 0.4, -H - 4 + wave * 1.6]);
  A.mouth = 'grin';
  A.look = [1, -0.3];
  I.look = [-1, -0.3];
  I.mouth = 'grin';
  I.sy = 1 + Math.max(0, wave) * 0.03;
};

const cheer: Behaviour = (A, I, t) => {
  hop(A, t, 1.1, 5);
  hop(I, t + 0.15, 1.1, 5);
  A.squint = 1;
  I.squint = 1;
  A.mouth = 'grin';
  I.mouth = 'grin';
};

const think: Behaviour = (A, I, t, fx) => {
  const u = (t % 3) / 3;
  I.look = [0, -1];
  I.open = 1.15;
  A.look = [1, -0.6];
  A.mouth = 'o';
  const s = easeBack(segment(u, 0.25, 0.45)) * (1 - segment(u, 0.85, 1));
  if (s > 0) {
    I.mouth = 'grin';
    fx.push((g, colors) => {
      const [x, y] = topOf(I);
      drawBulb(g, x, y - 5.5, s * 1.1, t, colors);
    });
  } else {
    I.mouth = 'flat';
  }
};

const work: Behaviour = (A, I, t) => {
  const w = Math.sin(t * 6);
  A.mouth = 'effort';
  I.mouth = 'effort';
  A.squint = w > 0.2 ? 1 : 0;
  A.look = [-1, -0.8];
  I.look = [1, -0.8];
  const hx = (A.cx + I.cx) / 2;
  const hy = -H - 5 + w * 0.8;
  raiseArm(A, -1, [hx - 3, hy]);
  raiseArm(I, 1, [hx + 3, hy]);
  A.rot = -0.05 - w * 0.02;
  I.rot = 0.05 + w * 0.02;
};

const proud: Behaviour = (A, I, t) => {
  A.blush = 1;
  I.blush = 1;
  A.mouth = 'grin';
  I.mouth = 'grin';
  const squint = t % 2.6 > 1.9 ? 1 : 0;
  A.squint = squint;
  I.squint = squint;
  A.look = [-0.6, 0.3];
  I.look = [0.6, 0.3];
};

const dizzy: Behaviour = (A, I, t, fx) => {
  I.dizzy = true;
  I.mouth = 'flat';
  I.rot = Math.sin(t * 3) * 0.08;
  A.mouth = 'grin';
  A.squint = 1;
  A.look = [1, 0];
  fx.push((g, colors) => {
    for (let j = 0; j < 3; j += 1) {
      const a = t * 6 + j * 2.1;
      drawSparkle(g, I.cx + Math.cos(a) * 5.5, -H - 2.5 + Math.sin(a) * 1.3, 0.9, j % 2 ? colors.accent : colors.spark);
    }
  });
};

const surprise: Behaviour = (A, I, t) => {
  const u = (t % 2.2) / 2.2;
  const jolt = u < 0.1 ? Math.sin((Math.PI * u) / 0.1) : 0;
  A.mouth = 'o';
  I.mouth = 'o';
  A.open = 1.2;
  I.open = 1.2;
  A.cy = -H2 - jolt * 1.6;
  I.cy = -H2 - jolt * 1.6;
  A.look = [1, -0.5];
  I.look = [-1, -0.5];
};

const point: Behaviour = (A, I, t) => {
  const u = Math.sin(t * 1.2);
  raiseArm(A, 1, [A.cx + 11, -H2 - 4 + u * 2]);
  A.look = [1, -u * 0.6];
  I.look = [1, -u * 0.6];
  A.mouth = 'o';
  I.mouth = 'smile';
};

const sleep: Behaviour = (A, I, t, fx) => {
  A.open = 0;
  I.open = 0;
  const breath = Math.sin(t * 1.4) * 0.02;
  A.sy = 1 + breath;
  I.sy = 1 + breath;
  A.rot = 0.06;
  I.rot = -0.05;
  fx.push((g, colors) => drawSleepZs(g, I.cx + 4, -H, t, colors));
};

const confused: Behaviour = (A, I, t, fx) => {
  I.rot = 0.14 + Math.sin(t * 2) * 0.02;
  I.mouth = 'wobble';
  I.look = [0.6, -0.6];
  A.mouth = 'flat';
  A.look = [1, -0.2];
  const ph = (t * 0.5) % 1;
  fx.push((g, colors) => {
    const [x, y] = topOf(I);
    drawQuestionMark(g, x + 3, y - 3 - ph * 2, 0.8 + ph * 0.3, 1 - ph, colors);
  });
};

const laugh: Behaviour = (A, I, t) => {
  const w = Math.sin(t * 10);
  A.mouth = 'grin';
  I.mouth = 'grin';
  A.squint = 1;
  I.squint = 1;
  A.rot = -0.12 + w * 0.04;
  I.rot = 0.1 - w * 0.04;
  A.sy = 1 + Math.max(0, w) * 0.05;
  I.sy = 1 + Math.max(0, -w) * 0.05;
  A.blush = 0.5;
  I.blush = 0.5;
};

const thumbs: Behaviour = (A, I, t) => {
  const u = Math.sin(t * 3);
  A.mouth = 'grin';
  A.look = [1, -0.3];
  A.squint = u > 0.6 ? 1 : 0;
  raiseArm(A, 1, [A.cx + 9, -H - 2 + u * 0.6]);
  A.rot = u * 0.03;
  I.mouth = 'smile';
  I.look = [-1, -0.3];
};

const run: Behaviour = (A, I, t) => {
  hop(A, t, 0.34, 1.6);
  hop(I, t + 0.06, 0.34, 1.6);
  for (const c of [A, I]) {
    c.rot = 0.16;
    c.look = [1, 0];
    c.mouth = 'effort';
    c.squint = 1;
  }
  const w = Math.sin(t * 18);
  raiseArm(A, -1, [A.cx - 7, -H2 - 2 + w * 2.5]);
  raiseArm(I, -1, [I.cx - 5, -H2 + w * 2.5]);
};

const flip: Behaviour = (A, I, t) => {
  const period = 2.4;
  const u = (t % period) / period;
  const one = (c: BuddyPose, offset: number) => {
    const v = (u + offset) % 1;
    if (v < 0.15) {
      const q = v / 0.15;
      c.sy = 1 - 0.2 * q;
      c.sx = 1 + 0.12 * q;
      c.mouth = 'effort';
      c.squint = 1;
    } else if (v < 0.75) {
      const a = (v - 0.15) / 0.6;
      c.cy = -H2 - Math.sin(Math.PI * a) * 14;
      c.rot = -Math.PI * 2 * easeInOutCubic(a);
      c.mouth = 'o';
      c.squint = 1;
    } else {
      const landed = (v - 0.75) * period;
      const k = Math.exp(-landed * 8) * Math.cos(landed * 18);
      c.sy = 1 - 0.2 * k;
      c.sx = 1 + 0.12 * k;
      c.mouth = 'grin';
      c.blush = 0.6;
      raiseArm(c, 1, [c.cx + BUDDY_HALF_WIDTH[c.kind] + 3, -H - 3]);
    }
  };
  one(A, 0);
  one(I, 0.5);
};

const cartwheel: Behaviour = (A, I, t) => {
  const u = (t % 2.6) / 2.6;
  const one = (c: BuddyPose, dir: 1 | -1) => {
    const e = easeInOutCubic(u < 0.5 ? u / 0.5 : 1 - (u - 0.5) / 0.5);
    c.rot = Math.PI * 2 * e * dir;
    c.cy = -H2 - Math.sin(Math.PI * e) * 4;
    c.cx += dir * (e - 0.5) * 14;
    c.mouth = 'grin';
    c.squint = 1;
    const reach = BUDDY_HALF_WIDTH[c.kind] + 4;
    raiseArm(c, -1, [c.cx - Math.cos(c.rot) * reach, c.cy - Math.sin(c.rot) * reach]);
    raiseArm(c, 1, [c.cx + Math.cos(c.rot) * reach, c.cy + Math.sin(c.rot) * reach]);
  };
  one(A, 1);
  one(I, -1);
};

const spin: Behaviour = (A, I, t) => {
  const one = (c: BuddyPose, ph: number) => {
    const turn = (t * 1.6 + ph) % 1;
    c.sx = Math.max(0.15, Math.abs(Math.cos(turn * Math.PI * 2)));
    c.mouth = 'smile';
    c.squint = 1;
    c.sy = 1 + Math.sin(t * 6 + ph) * 0.02;
    const [x, y] = topOf(c);
    raiseArm(c, -1, [x - 2, y - 6]);
    raiseArm(c, 1, [x + 2, y - 6]);
  };
  one(A, 0);
  one(I, 0.5);
};

export const BUDDY_BEHAVIOURS = {
  idle,
  listen,
  greet,
  cheer,
  think,
  work,
  proud,
  dizzy,
  surprise,
  point,
  sleep,
  confused,
  laugh,
  thumbs,
  run,
  flip,
  cartwheel,
  spin,
} as const satisfies Record<string, Behaviour>;

export type BuddyBehaviour = keyof typeof BUDDY_BEHAVIOURS;

type Act = readonly [BuddyBehaviour, number];

/** Long enough (about 36 s) that a build of a few minutes doesn't look like a loop. */
const BUSY_CYCLE: readonly Act[] = [
  ['greet', 1.4],
  ['work', 1.4],
  ['idle', 1.6],
  ['think', 3],
  ['surprise', 1.1],
  ['cheer', 1.2],
  ['cartwheel', 2.6],
  ['point', 1.6],
  ['work', 1.2],
  ['confused', 1.6],
  ['dizzy', 1.4],
  ['proud', 1.5],
  ['cheer', 1.3],
  ['laugh', 1.3],
  ['think', 3],
  ['sleep', 1.8],
  ['surprise', 1.1],
  ['spin', 1.5],
  ['proud', 1.2],
  ['thumbs', 1.3],
  ['flip', 2.4],
  ['work', 1.4],
];

export const BUSY_CYCLE_SECONDS = BUSY_CYCLE.reduce((sum, [, seconds]) => sum + seconds, 0);

export const busyActAt = (t: number): { behaviour: BuddyBehaviour; t: number } => {
  let rest = ((t % BUSY_CYCLE_SECONDS) + BUSY_CYCLE_SECONDS) % BUSY_CYCLE_SECONDS;
  for (const [behaviour, seconds] of BUSY_CYCLE) {
    if (rest < seconds) return { behaviour, t: rest };
    rest -= seconds;
  }
  return { behaviour: 'idle', t: rest };
};

export const TIP_ROUTINES: readonly Act[] = [
  ['cheer', 1.4],
  ['flip', 2.4],
  ['cartwheel', 2.6],
  ['greet', 1.6],
  ['spin', 1.5],
];

/** Mutates the poses in place; any drawing beyond the bodies is returned as effects. */
export const applyBehaviour = (behaviour: BuddyBehaviour, A: BuddyPose, I: BuddyPose, t: number): BuddyEffect[] => {
  const fx: BuddyEffect[] = [];
  BUDDY_BEHAVIOURS[behaviour](A, I, t, fx);
  return fx;
};
